import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { AdSlot } from "../components/AdSense";
import StructuredData from "../components/StructuredData";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { setPendingUploads } from "../lib/uploadStore";
import { api } from "../lib/api";
import { cdnImg, cdnSrcSet } from "../lib/img";
import UploadZone from "../components/UploadZone";
import AuthGateModal from "../components/AuthGateModal";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight,
  MagicWand,
  Sparkle,
  Lightning,
  Download,
  Palette,
  ShieldCheck,
  Drop,
  YoutubeLogo,
  Cube,
  ChartLineUp,
  Sun,
  Cloud,
  Heart,
  FilmReel,
  Pulse,
  CaretRight,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const COLOR_GRADED =
  "https://i.ibb.co/cSjpL1nD/Chat-GPT-Image-Jun-1-2026-06-54-57-PM-1.png";
const NON_COLOR_GRADED =
  "https://i.ibb.co/0RP2KyMm/Chat-GPT-Image-Jun-3-2026-02-35-17-AM.png";
const BRAND_BANNER =
  "https://i.ibb.co/qYRNq165/Chat-GPT-Image-Jun-3-2026-08-46-45-PM.png";
const YT_CHANNEL = "https://youtube.com/@gareebokafilmmaker";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [reference, setReference] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [authGateOpen, setAuthGateOpen] = useState(false);
  const [ytEmbedUrl, setYtEmbedUrl] = useState(
    "https://www.youtube.com/embed/videoseries?list=UULFc7Mn1lH7P0ZymrZc4dq-LA"
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site/config")
      .then(({ data }) => {
        if (!cancelled && data?.youtube_embed_url) setYtEmbedUrl(data.youtube_embed_url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const proceedToStudio = async () => {
    await setPendingUploads(video, reference, prompt);
    navigate("/studio");
  };

  const handleGenerate = () => {
    if (!video || !reference) {
      document.getElementById("upload-row")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (!user) {
      setAuthGateOpen(true);
      return;
    }
    proceedToStudio();
  };

  // AuthGate handles both Free and Creator end-to-end (Creator: signup → billing →
  // Cashfree → return). For Free we just proceed; for Creator the modal already
  // launched checkout, so we simply close the modal.
  const handleAuthSuccess = (selectedPlan) => {
    if (selectedPlan === "creator") {
      return; // checkout already launched by AuthGateModal
    }
    proceedToStudio();
  };

  return (
    <div className="App min-h-screen text-white bg-[#050505] overflow-x-hidden">
      <Navbar />

      {/* SEO: Organization + WebSite + SoftwareApplication JSON-LD */}
      <StructuredData
        id="landing-organization"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Gareebo Ka Filmmaker",
          alternateName: "CineLUT AI",
          url: typeof window !== "undefined" ? window.location.origin : "https://gareebokafilmmaker.com",
          logo:
            "https://wsrv.nl/?url=i.ibb.co/WvGVVmBf/too-much-highlights-and-white-202606030034-modified-1.png&w=512&output=webp&q=85&we=",
          description:
            "AI-powered cinematic 3D LUT generator and Creator OS for YouTubers, filmmakers and content creators.",
          sameAs: [
            "https://www.youtube.com/@neelvarma",
            "https://www.instagram.com/gareebokafilmmaker",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            email: "techneel23@gmail.com",
            contactType: "customer support",
            areaServed: "IN",
            availableLanguage: ["en", "hi"],
          },
        }}
      />
      <StructuredData
        id="landing-website"
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Gareebo Ka Filmmaker",
          url: typeof window !== "undefined" ? window.location.origin : "https://gareebokafilmmaker.com",
          potentialAction: {
            "@type": "SearchAction",
            target:
              (typeof window !== "undefined" ? window.location.origin : "https://gareebokafilmmaker.com") +
              "/blog?tag={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <StructuredData
        id="landing-software"
        data={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "CineLUT AI",
          applicationCategory: "MultimediaApplication",
          operatingSystem: "Web",
          description:
            "Upload a video and a reference frame. CineLUT AI generates a broadcast-quality 3D LUT (.cube / .3dl / .look) in seconds, with 11 colour sliders and 15 cinematic presets.",
          offers: {
            "@type": "Offer",
            price: "199",
            priceCurrency: "INR",
            category: "Subscription",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "126",
          },
        }}
      />

      {/* HERO */}
      <section className="relative">
        {/* Ambient red glow */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] bg-red-600/15 blur-[140px] rounded-full" />
          <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-red-500/10 blur-[120px] rounded-full" />
          <div className="absolute inset-0 grid-bg opacity-20" />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-12 pb-8 lg:pt-16 lg:pb-12 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 relative z-10"
          >
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-red-400 mb-6">
              <Sparkle size={12} weight="fill" />
              AI Color Matching that thinks like a colorist
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]">
              <span className="block">TURN ANY</span>
              <span className="block">
                LOOK <span className="text-red-500">INTO</span>
              </span>
              <span className="block text-zinc-300">YOUR LOOK</span>
            </h1>
            <p className="mt-7 max-w-md text-zinc-400 leading-relaxed">
              Upload your footage and a reference image. Our AI analyses colour, mood
              and tone to create a custom LUT that matches your vision — production
              ready in seconds.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-cta-start"
                size="lg"
                onClick={() => document.getElementById("upload-row")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-red-600 hover:bg-red-500 rounded-xl px-7 py-6 text-base shadow-[0_0_30px_rgba(255,77,57,0.4)] hover:shadow-[0_0_50px_rgba(255,77,57,0.6)]"
              >
                Generate My LUT <ArrowRight size={18} className="ml-1" />
              </Button>
              <a
                data-testid="hero-cta-youtube"
                href={YT_CHANNEL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white hover:bg-white/[0.04] transition"
              >
                <YoutubeLogo size={20} weight="fill" className="text-red-500" />
                <span className="text-sm">
                  Watch on YouTube <span className="text-zinc-500">· 2.5L subs</span>
                </span>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { icon: Cube, t: "AI MATCHING", s: "Advanced Color Intelligence" },
                { icon: Pulse, t: "CINEMATIC RESULTS", s: "Film-Grade Looks in Seconds" },
                { icon: Download, t: "PRO EXPORT", s: ".CUBE / .3DL / .LOOK Formats" },
              ].map(({ icon: Icon, t, s }, i) => (
                <div
                  key={i}
                  data-testid={`hero-feature-${i}`}
                  className="bg-white/[0.02] border border-white/10 rounded-xl p-4"
                >
                  <Icon size={20} className="text-red-500 mb-3" weight="duotone" />
                  <div className="text-[10px] font-mono tracking-widest uppercase text-red-400 leading-none mb-1.5">
                    {t}
                  </div>
                  <div className="text-xs text-zinc-300 leading-snug">{s}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Center visual — before/after split */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4 relative flex justify-center"
          >
            <div className="relative w-full max-w-[420px] aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(255,77,57,0.25)]">
              {/* Non-graded full layer */}
              <img
                src={cdnImg(NON_COLOR_GRADED, 840)}
                srcSet={cdnSrcSet(NON_COLOR_GRADED, [420, 640, 840])}
                sizes="(max-width: 768px) 420px, 420px"
                alt="Original frame"
                width="420"
                height="560"
                decoding="async"
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-image-original"
              />
              {/* Color-graded layer, animated reveal */}
              <motion.div
                className="absolute inset-0 overflow-hidden"
                initial={{ clipPath: "inset(0 100% 0 0)" }}
                animate={{
                  clipPath: [
                    "inset(0 100% 0 0)",
                    "inset(0 0% 0 0)",
                    "inset(0 0% 0 0)",
                    "inset(0 100% 0 0)",
                    "inset(0 100% 0 0)",
                  ],
                }}
                transition={{
                  duration: 6,
                  times: [0, 0.35, 0.5, 0.85, 1],
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 0.4,
                }}
                data-testid="hero-image-graded"
              >
                <img
                  src={cdnImg(COLOR_GRADED, 840)}
                  srcSet={cdnSrcSet(COLOR_GRADED, [420, 640, 840])}
                  sizes="(max-width: 768px) 420px, 420px"
                  alt="Color graded frame"
                  width="420"
                  height="560"
                  decoding="async"
                  fetchPriority="high"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </motion.div>
              {/* Animated sweep divider */}
              <motion.div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  width: 2,
                  background: "linear-gradient(180deg, transparent, rgba(255,77,57,1), transparent)",
                  boxShadow: "0 0 24px rgba(255,77,57,0.9)",
                }}
                initial={{ left: "0%" }}
                animate={{ left: ["0%", "100%", "100%", "0%", "0%"] }}
                transition={{
                  duration: 6,
                  times: [0, 0.35, 0.5, 0.85, 1],
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 0.4,
                }}
              />
              {/* corner labels */}
              <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest bg-black/70 px-2 py-1 rounded text-zinc-300">
                Original
              </div>
              <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest bg-red-600 px-2 py-1 rounded text-white">
                GKFM LUT
              </div>
              {/* tech ring overlay */}
              <div className="absolute -right-12 -bottom-12 w-56 h-56 rounded-full border-2 border-red-500/40 animate-pulse pointer-events-none" />
              <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full border border-red-500/60 pointer-events-none" />
            </div>
          </motion.div>

          {/* Right: AI Analysis card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-red-400">
                  AI Analysis
                </div>
                <div className="text-[10px] font-mono text-zinc-500">LIVE</div>
              </div>
              {[
                { icon: Palette, label: "Color Palette" },
                { icon: ChartLineUp, label: "Contrast" },
                { icon: Sun, label: "White Balance" },
                { icon: Heart, label: "Mood" },
                { icon: Drop, label: "Tone Curve" },
              ].map(({ icon: Icon, label }, i) => (
                <div
                  key={i}
                  data-testid={`hero-ai-${i}`}
                  className="flex items-center justify-between text-sm text-zinc-300"
                >
                  <span className="flex items-center gap-3">
                    <Icon size={16} className="text-red-500" weight="duotone" />
                    {label}
                  </span>
                  <CaretRight size={12} className="text-zinc-600" />
                </div>
              ))}
              <div className="pt-4 border-t border-white/5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
                  Matching
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold text-red-500 font-mono">97</span>
                  <span className="text-red-500 font-mono">%</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-600 to-red-400" style={{ width: "97%" }} />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* UPLOAD ROW — directly under hero */}
        <div id="upload-row" className="max-w-7xl mx-auto px-6 pb-10">
          <div className="relative grid lg:grid-cols-2 gap-5 items-stretch">
            <SideLabel label="SOURCE" side="left" />
            <SideLabel label="REFERENCE" side="right" />
            <div className="border-2 border-red-600/40 rounded-2xl bg-black/40 backdrop-blur-sm p-1">
              <UploadZone
                testId="upload-video"
                label="1. UPLOAD YOUR VIDEO OR PHOTO"
                hint="MP4 · MOV · WEBM · JPG · PNG  ·  Max 5s / 100MB"
                accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp"
                icon="media"
                file={video}
                onFile={setVideo}
              />
            </div>
            <div className="border-2 border-red-600/40 rounded-2xl bg-black/40 backdrop-blur-sm p-1">
              <UploadZone
                testId="upload-reference"
                label="2. UPLOAD REFERENCE PHOTO"
                hint="Upload a movie screenshot or any look you love"
                accept="image/jpeg,image/png,image/webp"
                icon="image"
                file={reference}
                onFile={setReference}
              />
            </div>
            {/* connector */}
            <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-red-600 text-white items-center justify-center shadow-[0_0_30px_rgba(255,77,57,0.7)] z-10">
              <span className="text-2xl font-light leading-none">+</span>
            </div>
          </div>

          <div className="mt-5 grid lg:grid-cols-[1fr_auto] gap-3 items-stretch">
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-red-400 mb-2 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> Tell our AI what you want (optional)
              </div>
              <Textarea
                data-testid="hero-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. give me a warmer Blade Runner mood with a slight vignette"
                rows={2}
                className="bg-transparent border-0 text-base text-zinc-200 resize-none focus-visible:ring-0 placeholder:text-zinc-600 p-0"
              />
            </div>
            <Button
              data-testid="hero-generate"
              size="lg"
              onClick={handleGenerate}
              className="bg-red-600 hover:bg-red-500 rounded-2xl px-10 py-7 text-base h-auto shadow-[0_0_36px_rgba(255,77,57,0.45)] hover:shadow-[0_0_60px_rgba(255,77,57,0.7)]"
            >
              <span className="flex items-center gap-3">
                <MagicWand size={22} weight="fill" />
                <span className="text-left leading-tight">
                  <span className="block font-semibold tracking-tight">GENERATE LUTS</span>
                  <span className="block text-[10px] font-mono tracking-[0.18em] uppercase opacity-80">
                    AI will create your custom look
                  </span>
                </span>
              </span>
            </Button>
          </div>

          {/* Trust bar */}
          <div className="mt-8 pt-6 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-6 text-xs">
            {[
              { i: ShieldCheck, t: "PRIVACY FIRST", s: "Your files are never shared" },
              { i: Drop, t: "NO WATERMARKS", s: "On paid exports" },
              { i: Lightning, t: "LIGHTNING FAST", s: "Results in 30–60 seconds" },
              { i: FilmReel, t: "MADE FOR CREATORS", s: "Film editors & colorists" },
            ].map(({ i: Icon, t, s }, idx) => (
              <div key={idx} data-testid={`trust-${idx}`} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-red-500" weight="duotone" />
                </div>
                <div>
                  <div className="font-mono tracking-[0.18em] text-zinc-300">{t}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <div className="label-tech mb-3 text-red-400">Workflow</div>
            <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter">
              Three steps, one cinematic LUT.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Upload Footage", d: "Drop a 10-second clip or still frame. MP4, MOV, WEBM, JPG, PNG — we handle the rest." },
              { n: "02", t: "Pick a Reference", d: "Any movie still, Netflix screenshot, or photograph that captures the look you want." },
              { n: "03", t: "Export LUT", d: ".cube, .3dl, .look — drop into DaVinci, Premiere, Final Cut. Done in under a minute." },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`how-step-${i}`}
                className="relative bg-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-red-500/40 transition-colors"
              >
                <div className="absolute -top-4 left-6 font-mono text-xs tracking-widest text-red-500 bg-black px-2 py-1 border border-red-500/40 rounded">
                  STEP {s.n}
                </div>
                <h3 className="text-xl font-medium tracking-tight mt-3 mb-2">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-t border-white/5 bg-gradient-to-b from-transparent via-red-950/[0.04] to-transparent">
        <div className="max-w-7xl mx-auto px-6"><AdSlot placement="landing" testId="landing-adslot" /></div>
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-10 items-end mb-12">
            <div>
              <div className="label-tech mb-3 text-red-400">Engine</div>
              <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter">
                Built by a filmmaker.<br /> For filmmakers.
              </h2>
            </div>
            <p className="text-zinc-400">
              True 3D LUT generation backed by LAB color science — anchored in real
              colorist principles, not random AI hallucinations. Skin tones stay alive,
              highlights stay protected, mood gets transferred.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <Feature
              icon={MagicWand}
              title="AI Color Matching"
              desc="LAB-space transfer plus optional Gemini Vision deep analysis builds a LUT that captures the exact look you uploaded."
              testId="feature-ai-matching"
            />
            <Feature
              icon={Cube}
              title="True 3D LUTs"
              desc="Industry-standard 33×33×33 .cube, .3dl, .look exports drop straight into DaVinci, Premiere, Final Cut, Lightroom."
              testId="feature-3d-lut"
            />
            <Feature
              icon={Lightning}
              title="Real-Time Preview"
              desc="Drag the Before/After slider. Tune 11 colorist controls. Watch the grade respond in real time."
              testId="feature-realtime"
            />
            <Feature
              icon={Palette}
              title="15 Cinematic Presets"
              desc="One-click looks — Hollywood, Teal & Orange, Nolan, Cyberpunk, Blade Runner, Dune, Kodak, Fuji, and more."
              testId="feature-presets"
            />
            <Feature
              icon={ChartLineUp}
              title="Scene Intelligence"
              desc="Automatic scene detection (Night, Outdoor Daylight, Sunset…) with smart recommendations for skin tones."
              testId="feature-scene"
            />
            <Feature
              icon={Download}
              title="Complete Export Kit"
              desc="LUT files, PNG before/after, extracted palette swatch, and a JSON grade recipe — every export, every time."
              testId="feature-export"
            />
          </div>
        </div>
      </section>

      {/* PERSONAL BRAND BANNER */}
      <section
        data-testid="brand-banner-section"
        className="border-t border-white/5 bg-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="flex flex-col items-center text-center mb-8 sm:mb-10">
            <div className="label-tech mb-3 text-red-400">
              The face behind the tool
            </div>
            <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter">
              Built by a filmmaker. <span className="text-red-400">For filmmakers.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-zinc-400 text-sm sm:text-base leading-relaxed">
              The colour science you see on the Gareebo Ka Filmmaker channel — now a
              one-click tool in your browser.
            </p>
          </div>

          <div
            data-testid="brand-banner-image-wrap"
            className="ember-grain group relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(255,77,57,0.18)] hover:shadow-[0_0_90px_rgba(255,77,57,0.35)] transition-shadow duration-700 bg-zinc-950"
          >
            <img
              data-testid="brand-banner-image"
              src={cdnImg(BRAND_BANNER, 1600)}
              srcSet={cdnSrcSet(BRAND_BANNER, [640, 960, 1280, 1600])}
              sizes="(max-width: 768px) 100vw, 1200px"
              alt="NeelVarma — Gareebo Ka Filmmaker"
              width="1600"
              height="600"
              decoding="async"
              className="w-full h-auto block"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/[0.04] rounded-2xl" />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-8">
            <a
              data-testid="brand-banner-yt-cta"
              href={YT_CHANNEL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white shadow-[0_0_30px_rgba(255,77,57,0.45)] transition text-sm font-medium"
            >
              <YoutubeLogo size={18} weight="fill" /> Follow on YouTube
              <ArrowRight size={14} />
            </a>
            <Link
              to="/studio"
              data-testid="brand-banner-studio-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 hover:border-red-500/60 text-zinc-200 hover:text-white transition text-sm font-medium"
            >
              <FilmReel size={16} className="text-red-400" /> Try the Studio
            </Link>
          </div>
        </div>
      </section>

      {/* YOUTUBE TRUST STRIP */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-tech mb-3 text-red-400">Made by Gareebo Ka Filmmaker (legally owned by Nilesh Rajendra Barnawal)</div>
            <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter mb-5">
              Built for the 2.5 Lakh<br /> filmmakers who watch every week.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-7">
              This tool is the same workflow Neel uses on his channel — the colour
              science behind every Gareebo Ka Filmmaker tutorial, packaged into a
              one-click LUT engine for you to use on your own footage.
            </p>
            <a
              data-testid="yt-channel-cta"
              href={YT_CHANNEL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white shadow-[0_0_30px_rgba(255,77,57,0.45)] transition"
            >
              <YoutubeLogo size={22} weight="fill" />
              <span className="text-sm font-medium">Subscribe on YouTube</span>
              <ArrowRight size={14} />
            </a>
          </div>
          <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(255,77,57,0.18)] bg-black">
            <iframe
              data-testid="yt-embed"
              src={ytEmbedUrl}
              title="Gareebo Ka Filmmaker"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <PricingPreview />

      {/* TESTIMONIALS */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-12">
            <div className="label-tech mb-3 text-red-400">Loved by Creators</div>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tighter">
              From YouTube comments. From our DMs.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { q: "Bhai 100% Hollywood look mil gaya — Resolve mein 2 ghante lagte the. Yeh 1 minute.", n: "Aarav K.", r: "Mumbai · Wedding Filmmaker" },
              { q: "Reference daala Blade Runner ka, output exactly waisa hi mila. Cheating jaisa lagta hai.", n: "Riya S.", r: "Delhi · YouTuber" },
              { q: "Studio plan with 60 AI credits is unreal for ₹199. Already exported 14 LUTs this week.", n: "Diego R.", r: "Mexico City · Colorist" },
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-red-500/30 transition"
              >
                <div className="text-red-500 text-3xl font-serif leading-none mb-3">&ldquo;</div>
                <p className="text-sm text-zinc-300 leading-relaxed mb-5">{t.q}</p>
                <div className="text-sm">
                  <div className="font-medium">{t.n}</div>
                  <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
                    {t.r}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <div className="label-tech mb-3 text-center text-red-400">FAQ</div>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tighter text-center mb-10">
            Questions, answered.
          </h2>
          {(() => {
            const faqs = [
              ["What video formats are supported?", "MP4, MOV, and WebM up to 10 seconds and 200 MB. We extract sample frames to analyse the look."],
              ["Will my skin tones get destroyed?", "No. We anchor the transfer in CIE LAB space and constrain saturation lift so skin tones remain natural."],
              ["What's the difference between Free and Creator plan?", "Free gives 2 LUTs per day with no AI prompt. Creator (₹199/mo) gives unlimited rule-based LUTs + 60 AI credits (each AI LUT consumes 10 credits, so 6 AI LUTs per month). Buy more credits anytime — ₹99 for 60 credits."],
              ["Can I use the exported LUT commercially?", "Yes on the Creator plan. Free tier is for personal use and learning."],
              ["What software do the LUTs work in?", "DaVinci Resolve, Premiere Pro, Final Cut Pro, Lightroom Classic, Photoshop, FilmConvert — any tool that accepts .cube / .3dl / .look."],
            ];
            return (
              <>
                <StructuredData
                  id="landing-faq"
                  data={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faqs.map(([q, a]) => ({
                      "@type": "Question",
                      name: q,
                      acceptedAnswer: { "@type": "Answer", text: a },
                    })),
                  }}
                />
                <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-2">
                  {faqs.map(([q, a], i) => (
                    <AccordionItem
                      key={i}
                      value={`q${i}`}
                      data-testid={`faq-item-${i}`}
                      className="border border-white/5 rounded-xl px-5 bg-white/[0.02]"
                    >
                      <AccordionTrigger className="text-left text-base">{q}</AccordionTrigger>
                      <AccordionContent className="text-zinc-400">{a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </>
            );
          })()}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter mb-4">
            Grade your next film in 60 seconds.
          </h2>
          <p className="text-zinc-400 mb-8">2 free LUTs every day. No credit card.</p>
          <Button
            data-testid="footer-cta"
            size="lg"
            onClick={() => document.getElementById("upload-row")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-red-600 hover:bg-red-500 rounded-xl px-8 py-6 shadow-[0_0_40px_rgba(255,77,57,0.45)]"
          >
            Start Grading <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      <Footer />

      <AuthGateModal
        open={authGateOpen}
        onOpenChange={setAuthGateOpen}
        onSuccess={handleAuthSuccess}
        pendingVideo={video}
        pendingReference={reference}
        pendingPrompt={prompt}
      />
    </div>
  );
}

const Feature = ({ icon: Icon, title, desc, testId }) => (
  <div
    data-testid={testId}
    className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-7 hover:border-red-500/40 hover:bg-white/[0.04] transition"
  >
    <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-5">
      <Icon size={22} weight="duotone" className="text-red-500" />
    </div>
    <h3 className="text-xl font-medium tracking-tight mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
  </div>
);

const SideLabel = ({ label, side }) => (
  <div
    className={`hidden md:block absolute top-1/2 -translate-y-1/2 ${
      side === "left" ? "-left-6 -rotate-90 origin-right" : "-right-6 rotate-90 origin-left"
    } pointer-events-none z-10`}
  >
    <span className="text-[10px] font-mono tracking-[0.4em] uppercase text-red-500/70">
      {label}
    </span>
  </div>
);

function PricingPreview() {
  const navigate = useNavigate();
  const TIERS = [
    {
      name: "Free",
      price: "₹0",
      period: "forever",
      testId: "tier-free",
      features: ["2 LUTs every day", "Rule-based color science", "No AI prompt", ".cube / .3dl / .look export", "Personal use"],
      cta: "Start Free",
    },
    {
      name: "Creator",
      price: "₹199",
      period: "/ month",
      highlight: true,
      testId: "tier-creator",
      features: ["Unlimited rule-based LUTs", "60 AI credits included", "10 credits per AI LUT (≈ 6 AI LUTs)", "Top-up: ₹99 for 60 credits", "Commercial usage"],
      cta: "Get Creator",
    },
  ];
  return (
    <section id="pricing" className="border-t border-white/5 bg-black">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <div className="label-tech mb-3 text-red-400">Pricing</div>
          <h2 className="text-3xl sm:text-5xl font-medium tracking-tighter">
            Simple. Just two plans.
          </h2>
          <p className="text-zinc-500 mt-3">Pay in INR via PhonePe · Cancel anytime</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={t.testId}
              className={`rounded-2xl p-8 border ${
                t.highlight
                  ? "bg-gradient-to-br from-red-900/30 via-red-800/10 to-black border-red-500/50 shadow-[0_0_60px_rgba(255,77,57,0.25)]"
                  : "bg-white/[0.02] border-white/10"
              }`}
            >
              <div className="text-sm font-mono uppercase tracking-widest text-red-400 mb-2">
                {t.name}
              </div>
              <div className="flex items-baseline gap-2 mb-7">
                <span className="text-5xl font-semibold tracking-tighter">{t.price}</span>
                <span className="text-sm text-zinc-500">{t.period}</span>
              </div>
              <ul className="space-y-2.5 text-sm text-zinc-300 mb-8">
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-red-500 mt-0.5">✓</span> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`${t.testId}-btn`}
                onClick={() => navigate("/pricing")}
                className={`w-full ${
                  t.highlight
                    ? "bg-red-600 hover:bg-red-500 shadow-[0_0_24px_rgba(255,77,57,0.4)]"
                    : "bg-white/[0.04] hover:bg-white/[0.08] border border-white/10"
                }`}
              >
                {t.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
